import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, FileSpreadsheet, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, apiError } from "@/lib/api";
import { formatIsoDay, formatTRY } from "@/lib/format";
import { AccDot, AccountBadge, METHODS, PayStatus, sel } from "@/lib/finance";
import { EmptyRow, LoadingRow, PageHeader, Pager } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import SearchPicker, { fetchCustomers } from "@/components/sales/SearchPicker";
import CollectionDialog from "@/components/finance/CollectionDialog";
import PaymentDetailDialog from "@/components/finance/PaymentDetailDialog";

const PAGE_SIZE = 25;
const TYPES = [["", "Tümü"], ["nakit", "Nakit"], ["kart", "Kart-POS"], ["havale", "Havale-EFT"], ["cek", "Çek"], ["senet", "Senet"]];
const EMPTY = { q: "", date_from: "", date_to: "", status: "", method: "", customer_id: "", account_id: "", collected_by: "", page: 1 };

function AccountMulti({ accounts, value, onChange }) {
  const sel_ = value ? value.split(",") : [];
  const toggle = (id) => onChange((sel_.includes(id) ? sel_.filter((x) => x !== id) : [...sel_, id]).join(","));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9" data-testid="payments-account-filter">Hesap{sel_.length ? ` (${sel_.length})` : ""}<ChevronDown className="h-3.5 w-3.5 ml-1" /></Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1 max-h-80 overflow-y-auto" data-testid="payments-account-options">
        {accounts.map((a) => (
          <label key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-[13px] cursor-pointer" data-testid={`payments-account-opt-${a.id}`}>
            <input type="checkbox" checked={sel_.includes(a.id)} onChange={() => toggle(a.id)} /><AccDot color={a.color} /><span className="truncate">{a.name}</span>
            {!a.is_active && <span className="text-[10px] text-slate-400 ml-auto">arşiv</span>}
          </label>
        ))}
        {sel_.length > 0 && <button className="w-full text-[12px] text-rose-600 py-1" onClick={() => onChange("")} data-testid="payments-account-clear">Seçimi temizle</button>}
      </PopoverContent>
    </Popover>
  );
}

function Filters({ f, setF, accounts, collectors, cari, setCari }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, page: 1 });
  return (
    <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-200" data-testid="payments-filters">
      <div className="flex flex-wrap gap-1.5 mr-2" data-testid="payments-type-chips">
        {TYPES.map(([k, l]) => (
          <button key={k || "all"} onClick={() => setF({ ...f, method: k, page: 1 })} data-testid={`payments-type-${k || "all"}`}
            className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${f.method === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{l}</button>
        ))}
      </div>
      <Input className="h-9 w-52" placeholder="Belge no, cari, evrak, açıklama..." value={f.q} onChange={set("q")} data-testid="payments-search-input" />
      <Input type="date" className="h-9 w-38" value={f.date_from} onChange={set("date_from")} data-testid="payments-date-from" />
      <Input type="date" className="h-9 w-38" value={f.date_to} onChange={set("date_to")} data-testid="payments-date-to" />
      {cari ? (
        <span className="inline-flex items-center gap-1 h-9 px-2 rounded-md border border-slate-200 bg-slate-50 text-[13px] max-w-[220px]" data-testid="payments-customer-chip">
          <span className="truncate">{cari.title}</span><button onClick={() => { setCari(null); setF({ ...f, customer_id: "", page: 1 }); }} data-testid="payments-customer-clear"><X className="h-3.5 w-3.5" /></button>
        </span>
      ) : (
        <div className="w-56"><SearchPicker testid="payments-customer-search" placeholder="Cari filtresi" fetcher={fetchCustomers}
          onPick={(c) => { setCari(c); setF({ ...f, customer_id: c.id, page: 1 }); }} render={(c) => <div className="text-[13px]">{c.title} <span className="font-mono text-[11px] text-slate-400">{c.code}</span></div>} /></div>
      )}
      <AccountMulti accounts={accounts} value={f.account_id} onChange={(v) => setF({ ...f, account_id: v, page: 1 })} />
      <select className={sel} value={f.collected_by} onChange={set("collected_by")} data-testid="payments-user-select">
        <option value="">Tüm kullanıcılar</option>{collectors.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <select className={sel} value={f.status} onChange={set("status")} data-testid="payments-status-select">
        <option value="">Tüm durumlar</option><option value="aktif">Aktif</option><option value="iptal">İptal</option>
      </select>
      {JSON.stringify({ ...f, page: 1 }) !== JSON.stringify(EMPTY) && <button className="text-[12px] text-blue-700 hover:underline" onClick={() => { setCari(null); setF(EMPTY); }} data-testid="payments-filters-reset">Filtreleri temizle</button>}
    </div>
  );
}

export const PaymentsTable = ({ res, onOpen, showCustomer = true }) => {
  const cols = showCustomer ? 10 : 9;
  return (
    <table className="erp-table" data-testid="payments-table">
      <thead><tr><th>Tarih</th><th>Belge No</th>{showCustomer && <th>Cari</th>}<th>Tür</th><th>Hesap</th><th className="text-right">Tutar</th><th>PB</th><th>Durum</th><th>Açıklama</th><th>Kullanıcı</th></tr></thead>
      <tbody>
        {!res ? <LoadingRow cols={cols} /> : res.items.length === 0 ? <EmptyRow cols={cols} text="Tahsilat kaydı yok" /> : res.items.map((p) => (
          <tr key={p.id} className={`cursor-pointer ${p.status === "iptal" ? "opacity-60" : ""}`} onClick={() => onOpen(p.id)} data-testid={`payment-row-${p.payment_no}`}>
            <td className="font-mono text-xs">{formatIsoDay(p.date)}</td>
            <td className="font-mono text-xs font-semibold text-slate-900">{p.payment_no}</td>
            {showCustomer && <td className="max-w-[220px] truncate">{p.customer_title}</td>}
            <td className="text-xs">{p.method_label}</td>
            <td className="max-w-[220px]" data-testid={`payment-account-${p.payment_no}`}>
              {p.account_name ? <AccountBadge name={p.account_name} color={p.account_color} archived={p.account_archived} /> : <span className="text-xs text-slate-500">{p.instrument_no || "—"}</span>}</td>
            <td className="text-right font-mono font-semibold">{formatTRY(p.amount)}</td>
            <td className="text-xs text-slate-500">{p.currency || "TRY"}</td>
            <td><PayStatus status={p.status} testid={`payment-status-${p.payment_no}`} /></td>
            <td className="max-w-[220px] truncate text-xs text-slate-500" title={p.note || ""}>{p.note || (p.match_fifo ? `FIFO · ${p.allocations.length}` : "")}</td>
            <td className="text-xs">{p.collected_by_name}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

function Totals({ t }) {
  if (!t) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-slate-200 bg-slate-50 text-[12px]" data-testid="payments-totals">
      <span className="font-semibold text-slate-900" data-testid="payments-total">Toplam (aktif): <span className="font-mono">{formatTRY(t.amount)}</span> · {t.count} kayıt</span>
      <span className="text-slate-300">|</span>
      {Object.entries(METHODS).filter(([k]) => t.by_method[k]).map(([k, l]) => (
        <span key={k} className="px-2 py-0.5 rounded-full bg-white border border-slate-200" data-testid={`payments-total-${k}`}>{l}: <b className="font-mono">{formatTRY(t.by_method[k].amount)}</b> ({t.by_method[k].count})</span>
      ))}
      {t.by_account.length > 0 && <span className="text-slate-300">|</span>}
      {t.by_account.map((a) => (
        <span key={a.account_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-slate-200" data-testid={`payments-total-acc-${a.account_id}`}>
          <AccDot color={a.color} />{a.name}: <b className="font-mono">{formatTRY(a.amount)}</b> ({a.count})</span>
      ))}
    </div>
  );
}

export default function Collections() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState(EMPTY);
  const [cari, setCari] = useState(null);
  const [res, setRes] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [reload, setReload] = useState(0);
  const openId = params.get("id");
  useEffect(() => { api.get("/tahsilatlar/accounts/all").then(({ data }) => setAccounts(data)).catch(() => {}); }, []);
  useEffect(() => {
    setRes(null);
    const t = setTimeout(() => api.get("/tahsilatlar", { params: { ...f, page_size: PAGE_SIZE } }).then(({ data }) => setRes(data)), 250);
    return () => clearTimeout(t);
  }, [f, reload]);
  const exportXlsx = async () => {
    try {
      const { page, ...q } = f;
      const r = await api.get("/tahsilatlar/export.xlsx", { params: q, responseType: "blob" });
      Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: `tahsilatlar_${new Date().toISOString().slice(0, 10)}.xlsx` }).click();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="collections-page">
      <PageHeader title="Tahsilatlar" subtitle={`Nakit, kart/POS, havale/EFT, çek ve senet tek listede${res?.scope === "own" ? " · yalnızca kendi carileriniz" : ""}`} testid="collections-title">
        <Button variant="outline" onClick={exportXlsx} data-testid="payments-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1 text-emerald-700" />Excel</Button>
        {can("tahsilatlar", "create") && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating(true)} data-testid="new-collection-button"><Plus className="h-4 w-4 mr-1" />Yeni Tahsilat</Button>}
      </PageHeader>
      <div className="panel overflow-hidden">
        <Filters f={f} setF={setF} accounts={accounts} collectors={res?.collectors || []} cari={cari} setCari={setCari} />
        <div className="overflow-x-auto max-h-[calc(100vh-300px)]"><PaymentsTable res={res} onOpen={(id) => setParams({ id })} /></div>
        <Totals t={res?.totals} />
        {res && <Pager page={f.page} pageSize={PAGE_SIZE} total={res.total} onPage={(p) => setF({ ...f, page: p })} testid="payments-pager" />}
      </div>
      {creating && <CollectionDialog onClose={() => setCreating(false)} onDone={(p) => { setCreating(false); setReload((r) => r + 1); setParams({ id: p.id }); }} />}
      {openId && <PaymentDetailDialog id={openId} onClose={() => setParams({})} onChanged={() => setReload((r) => r + 1)} />}
    </div>
  );
}
